import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Dns as DnsIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import { fetchJson } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

export default function SystemHealthPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState({
    overall: 'healthy',
    services: {},
    resources: {}
  });

  const [performanceData, setPerformanceData] = useState({
    responseTimes: [],
    errorRates: [],
    throughput: []
  });

  const [alerts, setAlerts] = useState([]);

  const [serverInfo, setServerInfo] = useState({});

  const [settings, setSettings] = useState({
    autoRefresh: true,
    refreshInterval: 30,
    alertThresholds: {
      cpu: 80,
      memory: 85,
      disk: 90,
      responseTime: 100
    },
    notifications: {
      email: true,
      sms: false,
      app: true
    }
  });

  const [settingsDialog, setSettingsDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState(false);
  const [newAlert, setNewAlert] = useState({ type: 'info', message: '', threshold: '' });

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('it-services');
  }, [user]);

  useEffect(() => {
    loadSystemHealth();
    if (settings.autoRefresh) {
      const interval = setInterval(loadSystemHealth, settings.refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [settings.autoRefresh, settings.refreshInterval]);

  const loadSystemHealth = async () => {
    try {
      setLoading(true);
      const [healthData, alertsData] = await Promise.all([
        fetchJson('/api/super-admin/system-health'),
        fetchJson('/api/super-admin/system-alerts').catch(() => ({ alerts: [] }))
      ]);

      if (healthData) {
        setSystemStatus({
          overall: healthData.overall || 'healthy',
          services: healthData.services || {},
          resources: healthData.resources || {}
        });
        setServerInfo(healthData.server || {});

        // Build performance chart data from activity history
        const history = healthData.activity?.performanceHistory || [];
        setPerformanceData({
          responseTimes: history.map((h, i) => ({
            hour: new Date(h.hour).getHours(),
            api: healthData.services?.api?.responseTime || 1,
            database: healthData.services?.database?.responseTime || 1,
            storage: healthData.services?.storage?.responseTime || 1
          })),
          errorRates: history.map((h) => ({
            hour: new Date(h.hour).getHours(),
            errors: 0,
            warnings: 0
          })),
          throughput: history.map((h) => ({
            hour: new Date(h.hour).getHours(),
            requests: h.requests || 0,
            users: healthData.activity?.activeUsers || 0
          }))
        });
      }

      // Map system alerts into the alerts state
      if (alertsData?.alerts) {
        setAlerts(alertsData.alerts.map((a, i) => ({
          id: a.id || i,
          type: a.priority === 'critical' ? 'error' : a.priority === 'high' ? 'warning' : 'info',
          message: a.message,
          time: a.time ? new Date(a.time).toLocaleString() : 'Unknown',
          acknowledged: false
        })));
      }
    } catch (error) {
      console.error('Failed to load system health:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
      case 'healthy':
      case 'normal':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
      case 'offline':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
      case 'healthy':
      case 'normal':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
      case 'offline':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const handleCreateAlert = async () => {
    if (!canManage) return;
    try {
      await fetchJson('/super-admin/system-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAlert)
      });
      setAlertDialog(false);
      setNewAlert({ type: 'info', message: '', threshold: '' });
      await loadSystemHealth();
    } catch (error) {
      console.error('Failed to create alert:', error);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    if (!canManage) return;
    try {
      await fetchJson(`/super-admin/system-alerts/${alertId}/acknowledge`, {
        method: 'PATCH'
      });
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId ? { ...alert, acknowledged: true } : alert
      ));
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>{t('systemHealth.loading', 'Loading system health data...')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          {t('systemHealth.title', 'System Health Monitoring')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSystemHealth}
          >
            {t('systemHealth.refresh', 'Refresh')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<NotificationsIcon />}
            onClick={() => setAlertDialog(true)}
            disabled={!canManage}
          >
            {t('systemHealth.createAlert', 'Create Alert')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsDialog(true)}
            disabled={!canManage}
          >
            {t('systemHealth.settings', 'Settings')}
          </Button>
        </Box>
      </Box>

      {/* Overall Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {getStatusIcon(systemStatus.overall)}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {t('systemHealth.overallStatus', 'Overall System Status')}: 
              <Chip 
                label={systemStatus.overall.toUpperCase()} 
                color={getStatusColor(systemStatus.overall)}
                sx={{ ml: 1 }}
              />
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Service Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('systemHealth.serviceStatus', 'Service Status')}</Typography>
              <List>
                {Object.entries(systemStatus.services).map(([service, data]) => (
                  <ListItem key={service} divider>
                    <ListItemIcon>
                      {getStatusIcon(data.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={service.toUpperCase()}
                      secondary={`Response: ${data.responseTime}ms | Uptime: ${data.uptime}%`}
                    />
                    <Chip 
                      label={data.status} 
                      color={getStatusColor(data.status)}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Resource Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('systemHealth.resourceUsage', 'Resource Usage')}</Typography>
              {Object.entries(systemStatus.resources).map(([resource, data]) => (
                <Box key={resource} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {resource}
                    </Typography>
                    <Typography variant="body2">
                      {data.usage}% / {data.threshold}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={data.usage}
                    color={getStatusColor(data.status)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Charts */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('systemHealth.responseTimeTrends', 'Response Time Trends (24h)')}</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData.responseTimes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="api" stroke="#8884d8" name="API" />
                  <Line type="monotone" dataKey="database" stroke="#82ca9d" name="Database" />
                  <Line type="monotone" dataKey="storage" stroke="#ffc658" name="Storage" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Error Rates */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('systemHealth.errorRates', 'Error Rates (24h)')}</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={performanceData.errorRates}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="errors" stackId="1" stroke="#ff7300" fill="#ff7300" name="Errors" />
                  <Area type="monotone" dataKey="warnings" stackId="1" stroke="#ffc658" fill="#ffc658" name="Warnings" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* System Alerts */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('systemHealth.systemAlerts', 'System Alerts')}</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('systemHealth.type', 'Type')}</TableCell>
                    <TableCell>{t('systemHealth.message', 'Message')}</TableCell>
                    <TableCell>{t('systemHealth.time', 'Time')}</TableCell>
                    <TableCell>{t('systemHealth.status', 'Status')}</TableCell>
                    <TableCell>{t('systemHealth.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell>
                        <Chip 
                          label={alert.type} 
                          color={getStatusColor(alert.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{alert.message}</TableCell>
                      <TableCell>{alert.time}</TableCell>
                      <TableCell>
                        <Chip 
                          label={alert.acknowledged ? t('systemHealth.acknowledged', 'Acknowledged') : t('systemHealth.pending', 'Pending')} 
                          color={alert.acknowledged ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {!alert.acknowledged && (
                          <Tooltip title={t('systemHealth.acknowledgeAlert', 'Acknowledge Alert')}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleAcknowledgeAlert(alert.id)}
                              disabled={!canManage}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('systemHealth.settingsTitle', 'System Health Settings')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>{t('systemHealth.monitoringSettings', 'Monitoring Settings')}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoRefresh}
                    onChange={(e) => setSettings({ ...settings, autoRefresh: e.target.checked })}
                    disabled={!canManage}
                  />
                }
                label={t('systemHealth.autoRefresh', 'Auto Refresh')}
              />
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>{t('systemHealth.refreshInterval', 'Refresh Interval (seconds)')}</InputLabel>
                <Select
                  value={settings.refreshInterval}
                  onChange={(e) => setSettings({ ...settings, refreshInterval: e.target.value })}
                  label={t('systemHealth.refreshInterval', 'Refresh Interval (seconds)')}
                  disabled={!canManage}
                >
                  <MenuItem value={15}>{t('systemHealth.15sec', '15 seconds')}</MenuItem>
                  <MenuItem value={30}>{t('systemHealth.30sec', '30 seconds')}</MenuItem>
                  <MenuItem value={60}>{t('systemHealth.1min', '1 minute')}</MenuItem>
                  <MenuItem value={300}>{t('systemHealth.5min', '5 minutes')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>{t('systemHealth.alertThresholds', 'Alert Thresholds')}</Typography>
              <TextField
                fullWidth
                label={t('systemHealth.cpuThreshold', 'CPU Threshold (%)')}
                type="number"
                value={settings.alertThresholds.cpu}
                onChange={(e) => setSettings({
                  ...settings,
                  alertThresholds: { ...settings.alertThresholds, cpu: Number(e.target.value) }
                })}
                sx={{ mb: 2 }}
                disabled={!canManage}
              />
              <TextField
                fullWidth
                label={t('systemHealth.memoryThreshold', 'Memory Threshold (%)')}
                type="number"
                value={settings.alertThresholds.memory}
                onChange={(e) => setSettings({
                  ...settings,
                  alertThresholds: { ...settings.alertThresholds, memory: Number(e.target.value) }
                })}
                sx={{ mb: 2 }}
                disabled={!canManage}
              />
              <TextField
                fullWidth
                label={t('systemHealth.responseTimeThreshold', 'Response Time Threshold (ms)')}
                type="number"
                value={settings.alertThresholds.responseTime}
                onChange={(e) => setSettings({
                  ...settings,
                  alertThresholds: { ...settings.alertThresholds, responseTime: Number(e.target.value) }
                })}
                disabled={!canManage}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)}>{t('systemHealth.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={() => setSettingsDialog(false)} disabled={!canManage}>{t('systemHealth.saveSettings', 'Save Settings')}</Button>
        </DialogActions>
      </Dialog>

      {/* Create Alert Dialog */}
      <Dialog open={alertDialog} onClose={() => setAlertDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('systemHealth.createAlertTitle', 'Create System Alert')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('systemHealth.alertType', 'Alert Type')}</InputLabel>
                <Select
                  value={newAlert.type}
                  onChange={(e) => setNewAlert({ ...newAlert, type: e.target.value })}
                  label={t('systemHealth.alertType', 'Alert Type')}
                  disabled={!canManage}
                >
                  <MenuItem value="info">{t('systemHealth.info', 'Info')}</MenuItem>
                  <MenuItem value="warning">{t('systemHealth.warning', 'Warning')}</MenuItem>
                  <MenuItem value="error">{t('systemHealth.error', 'Error')}</MenuItem>
                  <MenuItem value="success">{t('systemHealth.success', 'Success')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('systemHealth.alertMessage', 'Alert Message')}
                value={newAlert.message}
                onChange={(e) => setNewAlert({ ...newAlert, message: e.target.value })}
                disabled={!canManage}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('systemHealth.thresholdValue', 'Threshold Value (optional)')}
                value={newAlert.threshold}
                onChange={(e) => setNewAlert({ ...newAlert, threshold: e.target.value })}
                disabled={!canManage}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertDialog(false)}>{t('systemHealth.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleCreateAlert} disabled={!canManage}>{t('systemHealth.createAlert', 'Create Alert')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
